import React, { useCallback, useMemo, useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Flex, Switch } from "antd"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { usePermissions } from "hooks/usePermissions"
import useActiveGuide from "hooks/useActiveClient"
import { routes } from "router"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { Text } from "ui/Typography"
import { MODULES, ACTIONS } from "utils/permissions"
import moment from "moment"
import { DEFAULT_RANGE, CALENDAR_TABS } from "../constants"
import styles from "../Calendar.module.css"
import useHeaderFilters from "./hooks/useHeaderFilters"
import BookingTypeTabs from "./BookingTypeTabs"
import DateNavRow from "./DateNavRow"
import DesktopFiltersRow from "./DesktopFiltersRow"
import MobileFiltersDrawer from "./MobileFiltersDrawer"
import FilterChips from "./FilterChips"

const Header = ({
  params,
  onChangeFilters,
  handleJournalClick,
  setIsScheduleTimeOffModalOpen,
  onFiltersChange,
  startHour,
  endHour,
  onStartHourChange,
  onEndHourChange,
  onResetHours,
  hasMyBookings,
  savedFilters
}) => {
  const activeTab = params.tab || CALENDAR_TABS.ALL_BOOKINGS
  const isAgendaView = params.range === "week" && (params.agenda === true || params.agenda === "true")
  const isListView = params.range === "week" && params.listview
  const agendaChecked = params.agenda === true || params.agenda === "true"

  const showTimeRangeSettings = useMemo(() => {
    const isDayView = params.range === "date"
    const isWeekView = params.range === "week"

    if (activeTab === CALENDAR_TABS.ALL_BOOKINGS) {
      return isDayView || isAgendaView
    }
    if (activeTab === CALENDAR_TABS.MY_BOOKINGS) {
      return isDayView || isWeekView
    }
    if (activeTab === CALENDAR_TABS.CREW || activeTab === CALENDAR_TABS.EQUIPMENT) {
      return isDayView
    }
    return false
  }, [params.range, activeTab, isAgendaView])

  const { t } = useTranslation()
  const { isMobile, isTablet } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const { hasPermission } = usePermissions()
  const { isActive: active } = useActiveGuide()
  const navigate = useNavigate()

  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const isDesktop = !(isMobile || isTablet)

  const date = useMemo(() => {
    if (params.date && moment(params.date, DATE_FORMAT, true).isValid()) return params.date
    return moment().format(DATE_FORMAT)
  }, [params.date])
  const dateMoment = useMemo(() => moment(date, DATE_FORMAT, true), [date])
  const dateSwitcherType = isListView ? "date" : params.range
  const dateSwitcherKey = dateSwitcherType || "date"

  const getPopupContainer = useCallback((node) => node?.parentElement || document.body, [])
  const calendarFilterSelectProps = useMemo(() => ({ getPopupContainer }), [getPopupContainer])

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [viewPopoverOpen, setViewPopoverOpen] = useState(false)

  const {
    filters,
    crewFilters,
    setFilters,
    setCrewFilters,
    handleFilterChange,
    experienceOptions,
    crewMemberOptionsWithUnassigned,
    equipmentOptions,
    equipmentOptionsWithUnassigned,
    setEquipmentCategoryOptions,
    filterChips,
    roles,
    crewLoaded,
    equipmentLoaded
  } = useHeaderFilters({ savedFilters, activeBookingType: activeTab, onFiltersChange })

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewBooking = useCallback(() => navigate(routes.guideBookingNewPath()), [navigate])
  const handleGoToToday = useCallback(
    () => onChangeFilters({ target: { name: "date", value: moment().format(DATE_FORMAT) } }),
    [onChangeFilters]
  )
  const handleRangeChange = useCallback((key) => onChangeFilters({ target: { name: "range", value: key } }), [onChangeFilters])
  const handleAgendaChange = useCallback(
    (checked) => {
      onChangeFilters({ target: { name: "agenda", value: checked } })
      onFiltersChange({ agenda_view: checked })
    },
    [onChangeFilters, onFiltersChange]
  )

  const filterBadgeCount =
    activeTab === CALENDAR_TABS.EQUIPMENT
      ? (filters.equipmentFilters?.equipment?.length || 0) + (filters.equipmentFilters?.category?.length || 0)
      : filters.experience.length + filters.crew.length + filters.equipment.length

  const sharedFilterProps = {
    activeTab,
    params,
    filters,
    crewFilters,
    setFilters,
    setCrewFilters,
    handleFilterChange,
    experienceOptions,
    crewMemberOptionsWithUnassigned,
    equipmentOptions,
    equipmentOptionsWithUnassigned,
    roles,
    calendarFilterSelectProps,
    setEquipmentCategoryOptions,
    setIsScheduleTimeOffModalOpen,
    handleNewBooking,
    active,
    crewLoaded,
    equipmentLoaded
  }

  if (!params.range) return null

  return (
    <Flex vertical className={styles.calendarHeader}>
      <BookingTypeTabs activeTab={activeTab} hasMyBookings={hasMyBookings} onChangeFilters={onChangeFilters} />

      <DateNavRow
        params={params}
        activeTab={activeTab}
        isDesktop={isDesktop}
        isTablet={isTablet}
        isMobile={isMobile}
        date={date}
        dateMoment={dateMoment}
        dateSwitcherKey={dateSwitcherKey}
        dateSwitcherType={dateSwitcherType}
        filterBadgeCount={filterBadgeCount}
        agendaChecked={agendaChecked}
        viewPopoverOpen={viewPopoverOpen}
        setViewPopoverOpen={setViewPopoverOpen}
        setMobileFiltersOpen={setMobileFiltersOpen}
        onChangeFilters={onChangeFilters}
        onRangeChange={handleRangeChange}
        onAgendaChange={handleAgendaChange}
        onGoToToday={handleGoToToday}
      />

      {isDesktop && (
        <DesktopFiltersRow
          {...sharedFilterProps}
          canCreateBooking={canCreateBooking}
          isWideDesktop={isWideDesktop}
          showTimeRangeSettings={showTimeRangeSettings}
          startHour={startHour}
          endHour={endHour}
          onStartHourChange={onStartHourChange}
          onEndHourChange={onEndHourChange}
          onResetHours={onResetHours}
        />
      )}

      {(isMobile || isTablet) && <MobileFiltersDrawer open={mobileFiltersOpen} setOpen={setMobileFiltersOpen} {...sharedFilterProps} />}

      <FilterChips chips={filterChips} />

      {(isMobile || isTablet) && (
        <Flex align="center" wrap gap={8} justify="space-between" className="w-full p-s border-b">
          {params.range === "week" && activeTab === CALENDAR_TABS.ALL_BOOKINGS && !isListView && (
            <Flex align="center" gap={8} className={styles.agendaToggle}>
              <Switch checked={agendaChecked} onChange={handleAgendaChange} size="small" />
              <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  )
}

Header.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string,
    tab: PropTypes.string,
    agenda: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    listview: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  handleJournalClick: PropTypes.func,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  onStartHourChange: PropTypes.func,
  onEndHourChange: PropTypes.func,
  onResetHours: PropTypes.func,
  hasMyBookings: PropTypes.bool,
  savedFilters: PropTypes.object
}

export default Header
